import React from 'react';
import { Controller } from 'react-hook-form';
import { Button, Checkbox, Form, Header, Icon, Message, Segment, Table } from 'semantic-ui-react';
import timezoneOptions from 'pages/Schedule/timezoneOptions';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';
import { DAY_DISPLAY_ORDER, DAY_NAMES, TIME_OPTIONS } from './hoursUtils';

const BusinessHours = ({ control, businessHoursFields, businessHours, hasNoOpenDays, explanation }) => (
    <>
        <Header as='h4'>
            Business Hours <TierLabel tier={RECOMMENDATION_TIERS.RECOMMENDED} />
            <Header.Subheader>{explanation || 'When calls should ring your team.'} Calls outside these hours go to voicemail.</Header.Subheader>
        </Header>
        <Controller
            name='timezone'
            control={control}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <Form.Select
                    width={6}
                    label='Timezone'
                    required
                    name={name}
                    value={value}
                    options={timezoneOptions}
                    placeholder='Select timezone'
                    onChange={(_, { value }) => onChange({ target: { name, value } })}
                    error={error?.message}
                />
            )}
        />
        <Table compact unstackable className='Onboarding__hours'>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Day</Table.HeaderCell>
                    <Table.HeaderCell>Open</Table.HeaderCell>
                    <Table.HeaderCell>Opens</Table.HeaderCell>
                    <Table.HeaderCell>Closes</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {DAY_DISPLAY_ORDER.map(day => {
                    const index = businessHoursFields.findIndex(hours => hours.day === day);
                    if (index === -1) {
                        return null;
                    }
                    const isActive = businessHours[index]?.active;
                    return (
                        <Table.Row key={day} disabled={!isActive}>
                            <Table.Cell>{DAY_NAMES[day]}</Table.Cell>
                            <Table.Cell>
                                <Controller
                                    name={`business_hours.${index}.active`}
                                    control={control}
                                    render={({ field: { name, value, onChange } }) => (
                                        <Checkbox toggle checked={!!value} onChange={(_, { checked }) => onChange({ target: { name, value: checked } })} />
                                    )}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <Controller
                                    name={`business_hours.${index}.seconds_start`}
                                    control={control}
                                    render={({ field: { name, value, onChange } }) => (
                                        <Form.Select compact name={name} value={value} options={TIME_OPTIONS} disabled={!isActive} onChange={(_, { value }) => onChange({ target: { name, value } })} />
                                    )}
                                />
                            </Table.Cell>
                            <Table.Cell>
                                <Controller
                                    name={`business_hours.${index}.seconds_end`}
                                    control={control}
                                    render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                        <Form.Select compact name={name} value={value} options={TIME_OPTIONS} disabled={!isActive} onChange={(_, { value }) => onChange({ target: { name, value } })} error={error?.message} />
                                    )}
                                />
                            </Table.Cell>
                        </Table.Row>
                    );
                })}
            </Table.Body>
        </Table>
        {hasNoOpenDays && <Message warning size='small'>Open at least one day so calls can reach your team.</Message>}
    </>
);

// Phone numbers are set up with the customer by DYL's onboarding team for now (self-service is on the roadmap)
const BusinessHoursScreen = ({ onBack, onContinue, isStale, onRegenerate, ...props }) => (
    <Segment>
        <Header as='h3'>
            Business Hours
            <Header.Subheader>Tell us when your team is available so calls go to the right place.</Header.Subheader>
        </Header>
        <Message info size='small'>
            <Icon name='phone' />
            Your DYL onboarding team will set up your phone numbers with you. The hours and routing you choose here apply once your number is active.
        </Message>
        {isStale && (
            <Message warning size='small'>
                Your business profile or teams changed since these suggestions were made.
                <Button size='tiny' basic compact style={{ marginLeft: '1em' }} onClick={onRegenerate}>Regenerate Suggestions</Button>
            </Message>
        )}
        <Form noValidate>
            <BusinessHours {...props} />
        </Form>
        <div className='Onboarding__footer'>
            <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
            <Button primary floated='right' type='button' onClick={onContinue}>
                Continue to Call Routing <Icon name='arrow right' />
            </Button>
            <div style={{ clear: 'both' }} />
        </div>
    </Segment>
);

export default BusinessHoursScreen;
